/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Business Area Types
 */
export type BusinessAreaType = 'boarding' | 'dental' | 'daycare' | 'grooming';

/**
 * Address Attributes Interface
 */
export interface AddressAttributes {
    id?: number;
    marketplaceId: number;
    addressLineOne: string;
    addressLineTwo?: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
}

export interface LocationPagination {
    page: number;
    size: number;
    totalPages: number;
    totalRecords: number;
}
/**
 * Location Model
 */
export interface Location {
    id: number;
    organizationId: number;
    businessName: string;
    email: string;
    website?: string;
    phone: string;
    businessAreas: Array<BusinessAreaType>;
    active: boolean;
    createdAt: Date;
    updatedAt?: Date;
    addressesAttributes?: Array<AddressAttributes>;
    staffCount?: number;
}

export type LocationStaffSchedule = 'default' | 'custom';

export type LocationStaffStatus = 'active' | 'inactive' | 'all';

/**
 * Staff member of a location (marketplace)
 */
export interface LocationStaffMember {
    id: number;
    userId: number;
    firstName: string;
    lastName: string;
    name: string;
    role: string;
    schedule: LocationStaffSchedule;
    servicesCount: number;
    email: string;
    phone: string;
    active: boolean;
}

export interface LocationStaffFilters {
    status: LocationStaffStatus;
    name?: string;
    page: number;
    size: number;
}

export interface LocationStaffPage {
    staff: Array<LocationStaffMember>;
    pagination: LocationPagination;
}

/**
 * Save Location Model (for creating and editing locations)
 */
export interface SaveLocation {
    id?: number;
    active?: boolean;
    userId: number;
    organizationId: number;
    email: string;
    website?: string;
    businessName: string;
    businessAreas: Array<BusinessAreaType>;
    phone: string;
    // Address fields (used for backward compatibility and form data)
    address: string;
    suite?: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    termsOfService: boolean;
    privacyPolicy: boolean;
    // Address attributes for new API flow (will be sent in PATCH request)
    addressesAttributes?: Array<AddressAttributes>;
}

/**
 * Areas of a location's configuration that can be copied when duplicating it
 */
export type LocationCopyArea =
    | 'location_info'
    | 'business_hours'
    | 'settings_notifications'
    | 'taxes_tips'
    | 'pet_configuration'
    | 'services_addons'
    | 'online_booking_deposits'
    | 'agreements_forms'
    | 'notification_templates'
    | 'staff';

export interface LocationCopyAreaOption {
    id: LocationCopyArea;
    label: string;
}

// Order and labels used by the duplicate checklist and its summary
export const LOCATION_COPY_AREAS: Array<LocationCopyAreaOption> = [
    { id: 'location_info', label: 'Location info' },
    { id: 'business_hours', label: 'Business hours and special hours' },
    { id: 'settings_notifications', label: 'Settings and notifications' },
    { id: 'taxes_tips', label: 'Taxes and tips' },
    { id: 'pet_configuration', label: 'Pet configuration' },
    { id: 'services_addons', label: 'Services and add-ons' },
    { id: 'online_booking_deposits', label: 'Online booking and deposits' },
    { id: 'agreements_forms', label: 'Agreements and forms' },
    { id: 'notification_templates', label: 'Notification texts and email templates' },
    { id: 'staff', label: 'Staff' }
];

export type LocationDuplicationStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface LocationDuplicationReviewItem {
    area: LocationCopyArea;
    message: string;
}

/**
 * Status of a location duplication job
 */
export interface LocationDuplication {
    status: LocationDuplicationStatus;
    marketplaceId: number | null;
    copied: Array<LocationCopyArea>;
    review: Array<LocationDuplicationReviewItem>;
    error: string | null;
}

/**
 * Details of the new location entered in the duplicate form
 */
export interface DuplicateLocationDetails {
    businessName: string;
    email: string;
    phone: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
}
